import { useMemo, useState } from "react";
import {
  FiCheckCircle,
  FiPlay,
  FiPlus,
  FiRefreshCw,
  FiSearch,
  FiLayers,
  FiLock,
  FiAlertTriangle,
  FiArrowRight,
  FiX,
  FiShield,
  FiClock,
  FiRotateCcw,
  FiCheck,
  FiSlash,
  FiUploadCloud,
  FiEye,
  FiEdit2,
  FiArchive,
} from "react-icons/fi";
import {
  PageContainer,
  RuntimeDiagnosticBanner,
  type ComponentDiagnostic,
} from "@swot/base-components";
import { CmtsEnterpriseModal } from "@components/CmtsEnterpriseModal.js";
import { CmtsPageHintPanel } from "@components/hints/index.js";
import { normalizeUserSafeApiError } from "@cmts/api/apiErrorNormalizer.js";
import type { CmtsFormOption } from "@cmts/master-data/index.js";
import type {
  CmtsConfigurationDraftVm,
  CmtsConfigurationVersionVm,
} from "@cmts/mappers/configurationMappers.js";

export type CmtsConfigurationGovernanceMasterOptions = {
  readonly configurationType?: readonly CmtsFormOption[] | undefined;
  readonly activationMode?: readonly CmtsFormOption[] | undefined;
};

export type CmtsConfigurationDraftRow = {
  id?: string | undefined;
  draftId: string;
  title: string;
  configurationType: string;
  status: "Draft" | "Validated" | "Submitted" | "Approved" | "Scheduled" | "Activated" | "Rejected" | string;
  reviewer?: string | undefined;
  approver?: string | undefined;
  ownerId?: string | undefined;
  changeReason?: string | undefined;
  activationMode?: string | undefined;
  scheduledAt?: string | undefined;
  changePayload?: string | undefined;
  active?: boolean | undefined;
  versionId?: string | undefined;
  [key: string]: unknown;
};

type ActionCardProps = {
  readonly icon: string;
  readonly title: string;
  readonly description: string;
  readonly disabled?: boolean | undefined;
  readonly danger?: boolean | undefined;
  readonly onClick: () => void;
};

function ActionCard({
  icon,
  title,
  description,
  disabled = false,
  danger = false,
  onClick,
}: ActionCardProps) {
  return (
    <button
      type="button"
      className={[
        "cmts-notification-action-option",
        danger ? "cmts-notification-action-option--danger" : "",
      ]
        .filter(Boolean)
        .join(" ")}
      disabled={disabled}
      onClick={onClick}
    >
      <span className="cmts-notification-action-option__icon" aria-hidden="true">
        {icon}
      </span>

      <span className="cmts-notification-action-option__content">
        <strong>{title}</strong>
        <small>{description}</small>
      </span>

      <span className="cmts-notification-action-option__arrow" aria-hidden="true">
        ›
      </span>
    </button>
  );
}

const DEFAULT_DRAFTS: readonly CmtsConfigurationDraftRow[] = [
  {
    draftId: "DRAFT-2026-001",
    title: "Complaint Priority Routing Rule Matrix",
    configurationType: "Routing Matrix",
    status: "Approved",
    reviewer: "Sarah Jenkins (Lead QA)",
    approver: "Marcus Vance (VP Operations)",
    ownerId: "admin-sjenkins",
    changeReason: "Update priority escalation and SLA tiers for manufacturing defect complaints.",
    activationMode: "Scheduled",
    scheduledAt: "2026-08-25 00:00 UTC",
    changePayload: '{\n  "version": "2.4",\n  "routingRule": "SEV1_CRITICAL_ESCALATION",\n  "slaMinutes": 60\n}',
    active: true,
  },
  {
    draftId: "DRAFT-2026-002",
    title: "SLA Response Matrix - Extended 24x7 Shift",
    configurationType: "SLA Calendar",
    status: "Validated",
    reviewer: "David Chen (Governance Officer)",
    approver: "Elena Rostova (Compliance Director)",
    ownerId: "admin-dchen",
    changeReason: "Configure 24x7 holiday and weekend SLA coverage parameters for critical tier complaints.",
    activationMode: "Manual",
    scheduledAt: "Immediate",
    changePayload: '{\n  "calendarId": "SHIFT_24X7",\n  "coverageHours": 24,\n  "timezone": "UTC"\n}',
    active: true,
  },
  {
    draftId: "DRAFT-2026-003",
    title: "Master Category Hierarchy - Biologics Division",
    configurationType: "Master Schema",
    status: "Submitted",
    reviewer: "Amina Patel (Master Data Admin)",
    approver: "Marcus Vance (VP Operations)",
    ownerId: "admin-apatel",
    changeReason: "Add complaint subcategories for new biologic clinical trial products and lot inspection codes.",
    activationMode: "Immediate",
    scheduledAt: "Pending Approval",
    changePayload: '{\n  "division": "Biologics",\n  "newCategories": ["PRODUCT_STABILITY", "LABEL_INTEGRITY"]\n}',
    active: true,
  },
  {
    draftId: "DRAFT-2026-004",
    title: "Segregation of Duties Dual-Signoff Matrix v3.1",
    configurationType: "SoD Policy",
    status: "Activated",
    reviewer: "Audit & Security Committee",
    approver: "Chief Information Security Officer",
    ownerId: "admin-security",
    changeReason: "Enforce mandatory independent reviewer sign-off for CAPA investigations above threshold.",
    activationMode: "Immediate",
    scheduledAt: "2026-08-15 12:00 UTC",
    changePayload: '{\n  "sodVersion": "3.1",\n  "strictBlock": true,\n  "dualSignoffRequired": true\n}',
    active: true,
  },
  {
    draftId: "DRAFT-2026-005",
    title: "Automated Notification Webhook Endpoints",
    configurationType: "Notification Provider",
    status: "Draft",
    reviewer: "DevOps Lead",
    approver: "System Architect",
    ownerId: "admin-devops",
    changeReason: "Add redundant Slack and Webhook channels for urgent Sev1 complaint broadcasts.",
    activationMode: "Scheduled",
    scheduledAt: "Pending Submission",
    changePayload: '{\n  "provider": "Slack",\n  "channel": "#cmts-critical-alerts",\n  "enabled": true\n}',
    active: true,
  },
];

export function CmtsConfigurationGovernancePage(props: {
  drafts?: readonly CmtsConfigurationDraftVm[] | undefined;
  versions?: readonly CmtsConfigurationVersionVm[] | undefined;
  diagnostics?: readonly ComponentDiagnostic[] | undefined;
  onUpsert?: ((payload: Record<string, unknown>) => void | Promise<void>) | undefined;
  onAction?: ((row: Record<string, unknown>, action: string) => void | Promise<void>) | undefined;
  masterOptions?: CmtsConfigurationGovernanceMasterOptions | undefined;
  onRefresh?: (() => void | Promise<void>) | undefined;
}) {
  const rawRows: readonly CmtsConfigurationDraftRow[] = useMemo(() => {
    if (props.drafts && props.drafts.length > 0) {
      return props.drafts.map((r, i) => {
        const row = r as unknown as Record<string, unknown>;
        const draftId = String(row.draftId ?? row.id ?? row.code ?? row.draftCode ?? `DRAFT-${i + 1}`).trim();
        const title = String(row.title ?? row.changeSummary ?? row.displayName ?? row.draftCode ?? draftId).trim();
        const configurationType = String(row.configurationType ?? row.type ?? "Routing Matrix").trim();
        const status = String(row.status ?? "Draft").trim();
        const reviewer = String(row.reviewer ?? "QA Reviewer").trim();
        const approver = String(row.approver ?? "Governance Officer").trim();
        const ownerId = String(row.ownerId ?? row.createdBy ?? "System Admin").trim();
        const changeReason = String(row.changeReason ?? row.reason ?? "Configuration change request.").trim();
        const scheduledAt = String(row.scheduledAt ?? row.targetDate ?? "Immediate").trim();
        const changePayload = typeof row.changePayload === "string" ? row.changePayload : JSON.stringify(row.changePayload ?? {}, null, 2);
        const active = row.active !== false && status !== "Archived";
        return {
          ...row,
          draftId,
          title,
          configurationType,
          status,
          reviewer,
          approver,
          ownerId,
          changeReason,
          scheduledAt,
          changePayload,
          active,
        };
      });
    }
    return DEFAULT_DRAFTS;
  }, [props.drafts]);

  // Filters & State
  const [search, setSearch] = useState("");
  const [selectedStatus, setSelectedStatus] = useState<string>("ALL");
  const [selectedType, setSelectedType] = useState<string>("ALL");

  // Action Drawer / Modal State
  const [actionsFor, setActionsFor] = useState<CmtsConfigurationDraftRow | null>(null);

  // Create / Edit Modal State
  const [formOpen, setFormOpen] = useState(false);
  const [editingDraft, setEditingDraft] = useState<CmtsConfigurationDraftRow | null>(null);
  const [formDraftId, setFormDraftId] = useState("");
  const [formTitle, setFormTitle] = useState("");
  const [formType, setFormType] = useState("Routing Matrix");
  const [formReviewer, setFormReviewer] = useState("Sarah Jenkins (Lead QA)");
  const [formApprover, setFormApprover] = useState("Marcus Vance (VP Operations)");
  const [formChangeReason, setFormChangeReason] = useState("");
  const [formActivationMode, setFormActivationMode] = useState("Scheduled");
  const [formScheduledAt, setFormScheduledAt] = useState("");
  const [formChangePayload, setFormChangePayload] = useState('{\n  "enabled": true\n}');

  // Version History Modal State
  const [versionsModalOpen, setVersionsModalOpen] = useState(false);

  // Validation Simulation Modal State
  const [validationModalOpen, setValidationModalOpen] = useState(false);
  const [validationDraft, setValidationDraft] = useState<CmtsConfigurationDraftRow | null>(null);
  const [valResult, setValResult] = useState<{
    status: "passed" | "warn" | "blocked";
    title: string;
    message: string;
    items: readonly string[];
  } | null>(null);

  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  // Open Create Modal
  const openCreateModal = () => {
    setEditingDraft(null);
    setFormDraftId(`DRAFT-${new Date().getFullYear()}-${Date.now().toString().slice(-3)}`);
    setFormTitle("");
    setFormType("Routing Matrix");
    setFormReviewer("Sarah Jenkins (Lead QA)");
    setFormApprover("Marcus Vance (VP Operations)");
    setFormChangeReason("");
    setFormActivationMode("Scheduled");
    setFormScheduledAt(new Date(Date.now() + 86_400_000).toISOString().slice(0, 16));
    setFormChangePayload('{\n  "configVersion": "1.0",\n  "enabled": true\n}');
    setFormOpen(true);
  };

  // Open Edit Modal
  const openEditModal = (row: CmtsConfigurationDraftRow) => {
    setEditingDraft(row);
    setFormDraftId(row.draftId);
    setFormTitle(row.title);
    setFormType(row.configurationType);
    setFormReviewer(row.reviewer || "Sarah Jenkins (Lead QA)");
    setFormApprover(row.approver || "Marcus Vance (VP Operations)");
    setFormChangeReason(row.changeReason || "");
    setFormActivationMode(row.activationMode || "Scheduled");
    setFormScheduledAt(row.scheduledAt || "");
    setFormChangePayload(row.changePayload || '{\n  "enabled": true\n}');
    setFormOpen(true);
  };

  // Handle Save (Upsert)
  const handleSaveDraft = async () => {
    if (!formDraftId.trim() || !formTitle.trim()) return;
    setBusy(true);
    try {
      const payload: Record<string, unknown> = {
        ...(editingDraft ?? {}),
        draftId: formDraftId.trim(),
        title: formTitle.trim(),
        displayName: formTitle.trim(),
        configurationType: formType,
        reviewer: formReviewer.trim(),
        approver: formApprover.trim(),
        changeReason: formChangeReason.trim(),
        activationMode: formActivationMode,
        scheduledAt: formScheduledAt || undefined,
        changePayload: formChangePayload,
        status: editingDraft?.status ?? "Draft",
        group: "configuration",
        masterType: "configuration",
      };
      if (props.onUpsert) {
        await props.onUpsert(payload);
      }
      setMessage(`Configuration draft [${formDraftId}] saved successfully.`);
      setFormOpen(false);
      setActionsFor(null);
      if (props.onRefresh) await props.onRefresh();
    } catch (e) {
      setMessage(normalizeUserSafeApiError(e).userMessage || "Failed to save configuration draft.");
    } finally {
      setBusy(false);
    }
  };

  // Handle Lifecycle Action (validate, submit, approve, reject, schedule, activate, rollback)
  const handleLifecycleAction = async (row: CmtsConfigurationDraftRow, actionName: string) => {
    setBusy(true);
    try {
      if (props.onAction) {
        await props.onAction(row, actionName);
      }
      setMessage(`Action [${actionName.toUpperCase()}] completed for draft ${row.draftId}.`);
      setActionsFor(null);
      if (props.onRefresh) await props.onRefresh();
    } catch (e) {
      setMessage(normalizeUserSafeApiError(e).userMessage || `Action [${actionName}] failed.`);
    } finally {
      setBusy(false);
    }
  };

  // Run Validation Check Simulation
  const runValidationSimulation = (row: CmtsConfigurationDraftRow) => {
    setValidationDraft(row);
    if (row.status === "Draft") {
      setValResult({
        status: "passed",
        title: "Validation Passed: Ready for Submission",
        message: "JSON schema payload, dependency graph edges, and maker/checker roles verified clean.",
        items: [
          "Zero circular dependency references detected",
          "Reviewer and Approver personas conform to SoD segregation rules",
          "Payload schema validated against enterprise configuration specification",
        ],
      });
    } else if (row.status === "Approved") {
      setValResult({
        status: "passed",
        title: "Pre-Activation Integrity Check: Approved",
        message: "Dual-control approvals confirmed. Snapshot baseline captured for rollback.",
        items: [
          "VP Operations digital approval token verified",
          "Target production runtime partition readiness check: OK",
          "Zero-downtime hot-swap materialization plan initialized",
        ],
      });
    } else {
      setValResult({
        status: "warn",
        title: `Lifecycle State: ${row.status.toUpperCase()}`,
        message: `Draft is currently in ${row.status} state. Review audit trail before proceeding.`,
        items: [
          `Current owner: ${row.ownerId ?? "Admin"}`,
          `Scheduled timestamp: ${row.scheduledAt ?? "Immediate"}`,
          `Reviewer: ${row.reviewer ?? "Assigned"}`,
        ],
      });
    }
    setValidationModalOpen(true);
  };

  // Filtered Rows
  const filteredRows = useMemo(() => {
    return rawRows.filter((r) => {
      const matchesSearch =
        !search.trim() ||
        r.draftId.toLowerCase().includes(search.toLowerCase()) ||
        r.title.toLowerCase().includes(search.toLowerCase()) ||
        r.configurationType.toLowerCase().includes(search.toLowerCase()) ||
        String(r.reviewer ?? "").toLowerCase().includes(search.toLowerCase()) ||
        String(r.approver ?? "").toLowerCase().includes(search.toLowerCase()) ||
        String(r.changeReason ?? "").toLowerCase().includes(search.toLowerCase());

      const matchesStatus =
        selectedStatus === "ALL" ||
        r.status.toUpperCase() === selectedStatus.toUpperCase();

      const matchesType =
        selectedType === "ALL" ||
        r.configurationType.toLowerCase().includes(selectedType.toLowerCase());

      return matchesSearch && matchesStatus && matchesType;
    });
  }, [rawRows, search, selectedStatus, selectedType]);

  // Metrics
  const totalDrafts = rawRows.length;
  const activatedCount = rawRows.filter((r) => r.status === "Activated").length;
  const pendingApprovals = rawRows.filter((r) => r.status === "Submitted" || r.status === "Validated").length;
  const approvedReady = rawRows.filter((r) => r.status === "Approved" || r.status === "Scheduled").length;

  return (
    <PageContainer
      title="Configuration Governance"
      subtitle="Enterprise configuration versioning, dual-control approval lifecycle, and zero-downtime release automation."
    >
      <div className="cmts-sod-admin">
        {/* Diagnostic Banner */}
        <RuntimeDiagnosticBanner diagnostics={props.diagnostics ?? []} />

        <CmtsPageHintPanel defaultOpen={false}>
          Dual-control maker-checker approvals, automated schema validation, and instant rollback snapshots protect production runtime stability during configuration changes.
        </CmtsPageHintPanel>

        {/* Hero Header */}
        <section className="cmts-workspace-hero">
          <div>
            <p className="cmts-section-eyebrow">
              Maker-Checker Lifecycle &amp; Release Governance
            </p>
            <h2>Enterprise Configuration Lifecycle</h2>
            <p>
              Author configuration drafts, execute pre-release dependency checks, enforce dual-control authorizations, schedule production activations, and perform instant zero-downtime rollback snapshots.
            </p>
          </div>

          <div className="cmts-page-actions">
            <button
              type="button"
              className="cmts-action-button"
              onClick={openCreateModal}
            >
              <FiPlus /> Create Draft
            </button>
            <button
              type="button"
              className="cmts-secondary-button"
              onClick={() => setVersionsModalOpen(true)}
            >
              <FiLayers /> Version Snapshots
            </button>
            <button
              type="button"
              className="cmts-secondary-button"
              onClick={async () => {
                if (props.onRefresh) await props.onRefresh();
                setMessage("Configuration governance drafts refreshed.");
              }}
            >
              <FiRefreshCw /> Refresh
            </button>
          </div>
        </section>

        {/* Status Toast Message */}
        {message && (
          <div
            style={{
              padding: "0.85rem 1.25rem",
              background: "#f0fdf4",
              border: "1px solid #bbf7d0",
              borderRadius: 10,
              color: "#166534",
              fontSize: "0.875rem",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <span>{message}</span>
            <button
              type="button"
              onClick={() => setMessage(null)}
              style={{ background: "none", border: "none", cursor: "pointer", color: "#166534" }}
            >
              <FiX />
            </button>
          </div>
        )}

        {/* KPI Metrics */}
        <section className="cmts-sod-admin__kpis">
          <div className="cmts-sod-kpi">
            <div className="cmts-sod-kpi__icon cmts-sod-kpi__icon--blue">
              <FiLayers />
            </div>
            <div className="cmts-sod-kpi__body">
              <span className="cmts-sod-kpi__label">Governed Drafts</span>
              <div className="cmts-sod-kpi__val-row">
                <span className="cmts-sod-kpi__value">{totalDrafts}</span>
                <span className="cmts-sod-kpi__sub">Total in pipeline</span>
              </div>
            </div>
          </div>

          <div className="cmts-sod-kpi">
            <div className="cmts-sod-kpi__icon cmts-sod-kpi__icon--emerald">
              <FiCheckCircle />
            </div>
            <div className="cmts-sod-kpi__body">
              <span className="cmts-sod-kpi__label">Live Production</span>
              <div className="cmts-sod-kpi__val-row">
                <span className="cmts-sod-kpi__value">{activatedCount}</span>
                <span className="cmts-sod-kpi__sub">Active Versions</span>
              </div>
            </div>
          </div>

          <div className="cmts-sod-kpi">
            <div className="cmts-sod-kpi__icon cmts-sod-kpi__icon--amber">
              <FiClock />
            </div>
            <div className="cmts-sod-kpi__body">
              <span className="cmts-sod-kpi__label">Pending Approvals</span>
              <div className="cmts-sod-kpi__val-row">
                <span className="cmts-sod-kpi__value">{pendingApprovals}</span>
                <span className="cmts-sod-kpi__sub">Awaiting Sign-off</span>
              </div>
            </div>
          </div>

          <div className="cmts-sod-kpi">
            <div className="cmts-sod-kpi__icon cmts-sod-kpi__icon--rose">
              <FiPlay />
            </div>
            <div className="cmts-sod-kpi__body">
              <span className="cmts-sod-kpi__label">Release Ready</span>
              <div className="cmts-sod-kpi__val-row">
                <span className="cmts-sod-kpi__value">{approvedReady}</span>
                <span className="cmts-sod-kpi__sub">Scheduled / Approved</span>
              </div>
            </div>
          </div>
        </section>

        {/* Controls: Filter Pills + Search */}
        <section className="cmts-sod-admin__controls">
          <div className="cmts-sod-admin__filter-row">
            <div className="cmts-sod-admin__domain-pills">
              {[
                { id: "ALL", label: "All Lifecycle" },
                { id: "DRAFT", label: "Draft" },
                { id: "VALIDATED", label: "Validated" },
                { id: "SUBMITTED", label: "Submitted" },
                { id: "APPROVED", label: "Approved" },
                { id: "SCHEDULED", label: "Scheduled" },
                { id: "ACTIVATED", label: "Activated" },
              ].map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  className={`cmts-sod-pill ${selectedStatus === tab.id ? "cmts-sod-pill--active" : ""}`}
                  onClick={() => setSelectedStatus(tab.id)}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            <div className="cmts-sod-admin__search-box">
              <FiSearch style={{ color: "#94a3b8" }} />
              <input
                type="text"
                placeholder="Search drafts by ID, title, type, reviewer, or reason..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              {search && (
                <button
                  type="button"
                  onClick={() => setSearch("")}
                  style={{ background: "none", border: "none", cursor: "pointer", color: "#94a3b8" }}
                >
                  <FiX />
                </button>
              )}
            </div>
          </div>

          <div style={{ display: "flex", gap: "1rem", alignItems: "center", fontSize: "0.82rem", color: "#64748b" }}>
            <span><strong>Configuration Type:</strong></span>
            <button
              type="button"
              onClick={() => setSelectedType("ALL")}
              style={{ background: "none", border: "none", cursor: "pointer", fontWeight: selectedType === "ALL" ? 700 : 500, color: selectedType === "ALL" ? "#0f172a" : "#64748b" }}
            >
              All
            </button>
            <button
              type="button"
              onClick={() => setSelectedType("Routing")}
              style={{ background: "none", border: "none", cursor: "pointer", fontWeight: selectedType === "Routing" ? 700 : 500, color: selectedType === "Routing" ? "#0284c7" : "#64748b" }}
            >
              Routing Matrix
            </button>
            <button
              type="button"
              onClick={() => setSelectedType("SLA")}
              style={{ background: "none", border: "none", cursor: "pointer", fontWeight: selectedType === "SLA" ? 700 : 500, color: selectedType === "SLA" ? "#0284c7" : "#64748b" }}
            >
              SLA Calendar
            </button>
            <button
              type="button"
              onClick={() => setSelectedType("Schema")}
              style={{ background: "none", border: "none", cursor: "pointer", fontWeight: selectedType === "Schema" ? 700 : 500, color: selectedType === "Schema" ? "#0284c7" : "#64748b" }}
            >
              Master Schema
            </button>
            <button
              type="button"
              onClick={() => setSelectedType("SoD")}
              style={{ background: "none", border: "none", cursor: "pointer", fontWeight: selectedType === "SoD" ? 700 : 500, color: selectedType === "SoD" ? "#0284c7" : "#64748b" }}
            >
              SoD Policy
            </button>
          </div>
        </section>

        {/* Data Table Card */}
        <section className="cmts-sod-table-card">
          <table className="cmts-sod-table">
            <thead>
              <tr>
                <th>Draft & Change Summary</th>
                <th>Configuration Type</th>
                <th>Maker / Checker Flow</th>
                <th>Lifecycle Stage</th>
                <th>Target Release</th>
                <th style={{ textAlign: "center", width: 120 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredRows.length === 0 ? (
                <tr>
                  <td colSpan={6} style={{ textAlign: "center", padding: "3rem", color: "#64748b" }}>
                    <FiShield style={{ fontSize: "2.5rem", color: "#cbd5e1", display: "block", margin: "0 auto 0.75rem" }} />
                    <strong>No configuration drafts matched your criteria.</strong>
                    <p style={{ margin: "0.35rem 0 0", fontSize: "0.82rem" }}>Try adjusting your filters or create a new configuration draft.</p>
                  </td>
                </tr>
              ) : (
                filteredRows.map((row) => {
                  const stageClass =
                    row.status === "Activated"
                      ? "cmts-sod-badge--status-active"
                      : row.status === "Approved" || row.status === "Scheduled"
                        ? "cmts-sod-badge--domain-approval"
                        : row.status === "Submitted" || row.status === "Validated"
                          ? "cmts-sod-badge--result-warn"
                          : row.status === "Rejected"
                            ? "cmts-sod-badge--result-blocked"
                            : "cmts-sod-badge--domain-config";

                  return (
                    <tr key={row.draftId}>
                      <td>
                        <div className="cmts-sod-policy-cell">
                          <strong>{row.title}</strong>
                          <code>{row.draftId}</code>
                          <span className="cmts-sod-policy-desc">{row.changeReason}</span>
                        </div>
                      </td>
                      <td>
                        <span className="cmts-sod-badge cmts-sod-badge--domain-config">
                          {row.configurationType}
                        </span>
                      </td>
                      <td>
                        <div className="cmts-sod-constraint-tag">
                          <span>{row.reviewer || "QA Review"}</span>
                          <FiArrowRight style={{ color: "#94a3b8" }} />
                          <strong>{row.approver || "Executive Approver"}</strong>
                        </div>
                      </td>
                      <td>
                        <span className={`cmts-sod-badge ${stageClass}`}>
                          {row.status === "Activated" ? (
                            <FiCheckCircle />
                          ) : row.status === "Approved" ? (
                            <FiCheck />
                          ) : row.status === "Rejected" ? (
                            <FiSlash />
                          ) : (
                            <FiClock />
                          )}
                          {row.status.toUpperCase()}
                        </span>
                      </td>
                      <td>
                        <div style={{ fontSize: "0.82rem", color: "#334155", display: "flex", alignItems: "center", gap: "0.35rem" }}>
                          <FiClock style={{ color: "#64748b" }} />
                          <span>{row.scheduledAt || "Immediate"}</span>
                        </div>
                      </td>
                      <td style={{ textAlign: "center" }}>
                        <button
                          type="button"
                          className="cmts-notification-actions-button"
                          aria-label={`Open lifecycle actions for ${row.draftId}`}
                          onClick={(event) => {
                            event.stopPropagation();
                            setActionsFor(row);
                          }}
                        >
                          <span className="cmts-notification-actions-button__icon" aria-hidden="true">
                            ⋮
                          </span>
                          <span>Actions</span>
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </section>

        {/* =========================================================================
            Configuration Actions Popup Modal
           ========================================================================= */}
        <CmtsEnterpriseModal
          open={Boolean(actionsFor)}
          title="Configuration Lifecycle Actions"
          subtitle={actionsFor ? `${actionsFor.draftId} • ${actionsFor.title}` : ""}
          size="md"
          onClose={() => setActionsFor(null)}
        >
          {actionsFor && (
            <div className="cmts-notification-action-modal">
              {/* Metadata Summary Box */}
              <div className="cmts-notification-action-modal__summary">
                <div>
                  <span>Current Stage</span>
                  <strong style={{ color: actionsFor.status === "Activated" ? "#059669" : "#0284c7" }}>
                    {actionsFor.status?.toUpperCase() ?? "DRAFT"}
                  </strong>
                </div>

                <div>
                  <span>Config Type</span>
                  <strong>{actionsFor.configurationType}</strong>
                </div>

                <div>
                  <span>Approver</span>
                  <strong style={{ color: "#334155" }}>
                    {actionsFor.approver ?? "Governance Lead"}
                  </strong>
                </div>
              </div>

              {/* Action Cards Grid */}
              <div className="cmts-notification-action-modal__grid">
                <ActionCard
                  icon="⚡"
                  title="Validate draft integrity"
                  description="Run schema validation, dependency check, and circular graph analysis."
                  onClick={() => {
                    const target = actionsFor;
                    setActionsFor(null);
                    runValidationSimulation(target);
                  }}
                />

                <ActionCard
                  icon="✏"
                  title="Edit configuration draft"
                  description="Modify title, change reason, personas, and JSON payload."
                  onClick={() => {
                    const target = actionsFor;
                    setActionsFor(null);
                    openEditModal(target);
                  }}
                />

                <ActionCard
                  icon="📤"
                  title="Submit for approval"
                  description="Transition draft to Review stage for dual-control authorization."
                  disabled={actionsFor.status === "Activated" || actionsFor.status === "Approved"}
                  onClick={() => void handleLifecycleAction(actionsFor, "submit")}
                />

                <ActionCard
                  icon="✓"
                  title="Approve configuration"
                  description="Authorize configuration for release and production scheduling."
                  disabled={actionsFor.status === "Activated"}
                  onClick={() => void handleLifecycleAction(actionsFor, "approve")}
                />

                <ActionCard
                  icon="🚀"
                  title="Activate to production"
                  description="Trigger live zero-downtime hot-swap activation."
                  disabled={actionsFor.status === "Activated"}
                  onClick={() => void handleLifecycleAction(actionsFor, "activate")}
                />

                <ActionCard
                  icon="📅"
                  title="Schedule activation"
                  description="Set specific production release window timestamp."
                  onClick={() => void handleLifecycleAction(actionsFor, "schedule")}
                />
              </div>

              {/* Danger / Reject / Rollback Action Card at Bottom */}
              <div className="cmts-notification-action-modal__danger">
                <ActionCard
                  icon={actionsFor.status === "Activated" ? "↺" : "✕"}
                  title={actionsFor.status === "Activated" ? "Rollback version snapshot" : "Reject configuration draft"}
                  description={
                    actionsFor.status === "Activated"
                      ? "Revert live runtime to the previous verified configuration snapshot."
                      : "Decline this change request and notify the author."
                  }
                  danger
                  onClick={() => {
                    const actionName = actionsFor.status === "Activated" ? "rollback" : "reject";
                    void handleLifecycleAction(actionsFor, actionName);
                  }}
                />
              </div>
            </div>
          )}
        </CmtsEnterpriseModal>

        {/* =========================================================================
            Create / Edit Draft Modal
           ========================================================================= */}
        <CmtsEnterpriseModal
          open={formOpen}
          title={editingDraft ? `Edit Draft: ${editingDraft.draftId}` : "Create Configuration Draft"}
          subtitle="Define master change parameters, dual-control personas, and JSON payload"
          size="lg"
          onClose={() => setFormOpen(false)}
        >
          <div style={{ display: "flex", flexDirection: "column", gap: "1.25rem", padding: "0.5rem" }}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem" }}>
              <div>
                <label style={{ display: "block", fontSize: "0.82rem", fontWeight: 600, color: "#334155", marginBottom: "0.35rem" }}>
                  Draft Identifier *
                </label>
                <input
                  type="text"
                  value={formDraftId}
                  onChange={(e) => setFormDraftId(e.target.value.toUpperCase())}
                  placeholder="e.g. DRAFT-2026-006"
                  style={{ width: "100%", padding: "0.6rem 0.85rem", border: "1px solid #cbd5e1", borderRadius: 8, fontSize: "0.875rem" }}
                  disabled={editingDraft !== null}
                />
              </div>

              <div>
                <label style={{ display: "block", fontSize: "0.82rem", fontWeight: 600, color: "#334155", marginBottom: "0.35rem" }}>
                  Configuration Type *
                </label>
                <select
                  value={formType}
                  onChange={(e) => setFormType(e.target.value)}
                  style={{ width: "100%", padding: "0.6rem 0.85rem", border: "1px solid #cbd5e1", borderRadius: 8, fontSize: "0.875rem" }}
                >
                  <option value="Routing Matrix">Routing Matrix (Complaint Escalation)</option>
                  <option value="SLA Calendar">SLA Calendar (Working Hours & Shifts)</option>
                  <option value="Master Schema">Master Schema (Categories & Fields)</option>
                  <option value="SoD Policy">SoD Policy (Segregation of Duties)</option>
                  <option value="Notification Provider">Notification Provider (Webhooks & SMS)</option>
                </select>
              </div>
            </div>

            <div>
              <label style={{ display: "block", fontSize: "0.82rem", fontWeight: 600, color: "#334155", marginBottom: "0.35rem" }}>
                Draft Title / Purpose *
              </label>
              <input
                type="text"
                value={formTitle}
                onChange={(e) => setFormTitle(e.target.value)}
                placeholder="e.g. Update priority escalation rules for manufacturing defect complaints"
                style={{ width: "100%", padding: "0.6rem 0.85rem", border: "1px solid #cbd5e1", borderRadius: 8, fontSize: "0.875rem" }}
              />
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem" }}>
              <div>
                <label style={{ display: "block", fontSize: "0.82rem", fontWeight: 600, color: "#334155", marginBottom: "0.35rem" }}>
                  Independent Reviewer *
                </label>
                <input
                  type="text"
                  value={formReviewer}
                  onChange={(e) => setFormReviewer(e.target.value)}
                  placeholder="e.g. Sarah Jenkins (Lead QA)"
                  style={{ width: "100%", padding: "0.6rem 0.85rem", border: "1px solid #cbd5e1", borderRadius: 8, fontSize: "0.875rem" }}
                />
              </div>

              <div>
                <label style={{ display: "block", fontSize: "0.82rem", fontWeight: 600, color: "#334155", marginBottom: "0.35rem" }}>
                  Executive Approver *
                </label>
                <input
                  type="text"
                  value={formApprover}
                  onChange={(e) => setFormApprover(e.target.value)}
                  placeholder="e.g. Marcus Vance (VP Operations)"
                  style={{ width: "100%", padding: "0.6rem 0.85rem", border: "1px solid #cbd5e1", borderRadius: 8, fontSize: "0.875rem" }}
                />
              </div>
            </div>

            <div>
              <label style={{ display: "block", fontSize: "0.82rem", fontWeight: 600, color: "#334155", marginBottom: "0.35rem" }}>
                Change Rationale & Compliance Justification *
              </label>
              <textarea
                value={formChangeReason}
                onChange={(e) => setFormChangeReason(e.target.value)}
                placeholder="Explain the business need, risk assessment, and regulatory impact of this master change..."
                rows={3}
                style={{ width: "100%", padding: "0.6rem 0.85rem", border: "1px solid #cbd5e1", borderRadius: 8, fontSize: "0.875rem", fontFamily: "inherit" }}
              />
            </div>

            <div>
              <label style={{ display: "block", fontSize: "0.82rem", fontWeight: 600, color: "#334155", marginBottom: "0.35rem" }}>
                Configuration JSON Payload *
              </label>
              <textarea
                value={formChangePayload}
                onChange={(e) => setFormChangePayload(e.target.value)}
                rows={4}
                style={{ width: "100%", padding: "0.6rem 0.85rem", border: "1px solid #cbd5e1", borderRadius: 8, fontSize: "0.85rem", fontFamily: "monospace" }}
              />
            </div>

            <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.75rem", marginTop: "0.5rem" }}>
              <button
                type="button"
                className="cmts-sod-btn cmts-sod-btn--secondary"
                onClick={() => setFormOpen(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="cmts-sod-btn cmts-sod-btn--primary"
                disabled={busy || !formDraftId.trim() || !formTitle.trim()}
                onClick={handleSaveDraft}
              >
                {busy ? "Saving Draft..." : editingDraft ? "Save Draft" : "Create Draft"}
              </button>
            </div>
          </div>
        </CmtsEnterpriseModal>

        {/* =========================================================================
            Validation Results Modal
           ========================================================================= */}
        <CmtsEnterpriseModal
          open={validationModalOpen}
          title="Configuration Validation & Dependency Report"
          subtitle={validationDraft ? `${validationDraft.draftId} • ${validationDraft.title}` : ""}
          size="md"
          onClose={() => setValidationModalOpen(false)}
        >
          {valResult && (
            <div style={{ display: "flex", flexDirection: "column", gap: "1.25rem", padding: "0.5rem" }}>
              <div
                style={{
                  padding: "1rem 1.25rem",
                  borderRadius: 12,
                  background: valResult.status === "passed" ? "#f0fdf4" : "#fffbeb",
                  border: `1px solid ${valResult.status === "passed" ? "#bbf7d0" : "#fde68a"}`,
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginBottom: "0.35rem" }}>
                  <FiCheckCircle style={{ color: "#166534" }} />
                  <strong style={{ color: "#166534" }}>{valResult.title}</strong>
                </div>
                <p style={{ margin: 0, fontSize: "0.875rem", color: "#334155" }}>{valResult.message}</p>
              </div>

              <div>
                <span style={{ fontSize: "0.75rem", fontWeight: 700, color: "#64748b", textTransform: "uppercase" }}>
                  Integrity Verification Checklist
                </span>
                <ul style={{ margin: "0.5rem 0 0", paddingLeft: "1.25rem", fontSize: "0.85rem", color: "#334155", lineHeight: 1.6 }}>
                  {valResult.items.map((item, idx) => (
                    <li key={idx}>{item}</li>
                  ))}
                </ul>
              </div>

              <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.75rem" }}>
                <button
                  type="button"
                  className="cmts-sod-btn cmts-sod-btn--primary"
                  onClick={() => setValidationModalOpen(false)}
                >
                  Done
                </button>
              </div>
            </div>
          )}
        </CmtsEnterpriseModal>

        {/* =========================================================================
            Version History & Rollback Modal
           ========================================================================= */}
        <CmtsEnterpriseModal
          open={versionsModalOpen}
          title="Production Version Snapshots"
          subtitle="Audit history of activated configuration releases with rollback safety points"
          size="lg"
          onClose={() => setVersionsModalOpen(false)}
        >
          <div style={{ display: "flex", flexDirection: "column", gap: "1rem", padding: "0.5rem" }}>
            <div style={{ padding: "0.85rem 1rem", background: "#f8fafc", borderRadius: 8, border: "1px solid #e2e8f0", fontSize: "0.82rem", color: "#475569" }}>
              <FiShield style={{ marginRight: 6, color: "#0284c7" }} />
              Each production activation creates an immutable snapshot. Rolling back restores full configuration state with zero downtime.
            </div>

            <table className="cmts-sod-table" style={{ fontSize: "0.82rem" }}>
              <thead>
                <tr>
                  <th>Version</th>
                  <th>Description</th>
                  <th>Activated By</th>
                  <th>Activated At</th>
                  <th style={{ textAlign: "center" }}>Rollback</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td><strong>v3.1.0</strong> (Live)</td>
                  <td>Segregation of Duties Dual-Signoff Matrix v3.1</td>
                  <td>admin-security</td>
                  <td>2026-08-15 12:00 UTC</td>
                  <td style={{ textAlign: "center" }}>
                    <span className="cmts-sod-badge cmts-sod-badge--status-active">Current Live</span>
                  </td>
                </tr>
                <tr>
                  <td><strong>v3.0.4</strong></td>
                  <td>Master Category Hierarchy - Biologics Division</td>
                  <td>admin-apatel</td>
                  <td>2026-08-01 08:30 UTC</td>
                  <td style={{ textAlign: "center" }}>
                    <button
                      type="button"
                      className="cmts-sod-btn cmts-sod-btn--secondary"
                      style={{ padding: "0.3rem 0.65rem", fontSize: "0.75rem" }}
                      onClick={() => {
                        setMessage("Rollback to snapshot v3.0.4 initiated.");
                        setVersionsModalOpen(false);
                      }}
                    >
                      <FiRotateCcw /> Rollback
                    </button>
                  </td>
                </tr>
                <tr>
                  <td><strong>v3.0.0</strong></td>
                  <td>Initial Baseline Production Matrix 2026</td>
                  <td>system-root</td>
                  <td>2026-06-01 00:00 UTC</td>
                  <td style={{ textAlign: "center" }}>
                    <button
                      type="button"
                      className="cmts-sod-btn cmts-sod-btn--secondary"
                      style={{ padding: "0.3rem 0.65rem", fontSize: "0.75rem" }}
                      onClick={() => {
                        setMessage("Rollback to baseline v3.0.0 initiated.");
                        setVersionsModalOpen(false);
                      }}
                    >
                      <FiRotateCcw /> Rollback
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>

            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "0.5rem" }}>
              <button
                type="button"
                className="cmts-sod-btn cmts-sod-btn--primary"
                onClick={() => setVersionsModalOpen(false)}
              >
                Close
              </button>
            </div>
          </div>
        </CmtsEnterpriseModal>
      </div>
    </PageContainer>
  );
}
